import * as THREE from 'three';
import type { Sketch, SketchContext, SketchMeta } from './Sketch';
import { TouchField } from '../core/input/TouchField';
import { IdleTimer } from '../core/kiosk/idle';
import { Quality } from '../core/util/quality';
import { FpsMeter } from '../core/util/fps';
import { PRNG } from '../core/util/prng';
import { effectiveDpr } from '../core/util/dpr';
import { Menu } from '../ui/Menu';
import { Overlay } from '../ui/Overlay';

/**
 * The harness. Owns the single WebGLRenderer and the only requestAnimationFrame
 * loop, switches sketches (disposing the previous one), drives input/idle/quality
 * each frame, and toggles the menu vs. in-sketch overlay UI.
 */
export class App {
  readonly renderer: THREE.WebGLRenderer;
  private readonly input: TouchField;
  private readonly idle: IdleTimer;
  private readonly quality = new Quality();
  private readonly fpsMeter = new FpsMeter();
  private readonly rng = new PRNG();

  private readonly menu: Menu;
  private readonly overlay: Overlay;

  private current: Sketch | null = null;
  private currentId: string | null = null;
  private loadToken = 0;

  private readonly start = performance.now();
  private last = this.start;
  private raf = 0;

  constructor(
    root: HTMLElement,
    onOpen: (id: string) => void,
    onHome: () => void,
  ) {
    const canvas = document.createElement('canvas');
    canvas.className = 'viz-canvas';
    root.appendChild(canvas);

    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: false,
      alpha: false,
      powerPreference: 'high-performance',
    });
    this.renderer.setClearColor(0x000000, 1);
    this.renderer.autoClear = true;

    this.input = new TouchField(canvas);
    this.idle = new IdleTimer(this.input);

    this.menu = new Menu(root, onOpen);
    this.overlay = new Overlay(root, onHome);

    window.addEventListener('resize', this.onResize);
    document.addEventListener('visibilitychange', this.onVisibility);
    this.applySize();
  }

  // --- lifecycle --------------------------------------------------------

  loop(): void {
    this.last = performance.now();
    const frame = () => {
      this.raf = requestAnimationFrame(frame);
      this.tick();
    };
    this.raf = requestAnimationFrame(frame);
  }

  private tick(): void {
    const now = performance.now();
    const dt = Math.min((now - this.last) / 1000, 1 / 20); // clamp huge gaps
    this.last = now;
    const t = (now - this.start) / 1000;

    this.input.update(dt);
    this.idle.update();

    const fps = this.fpsMeter.sample(dt);
    if (this.quality.update(fps, dt)) this.applySize();

    if (this.current) {
      this.current.update(dt, t);
      this.current.render();
    }
    this.overlay.update(fps);
  }

  // --- routing ----------------------------------------------------------

  async open(meta: SketchMeta): Promise<void> {
    if (this.currentId === meta.id) return;
    const token = ++this.loadToken;

    this.disposeCurrent();
    this.menu.hide();
    this.overlay.show(meta.title);

    try {
      const mod = await meta.load();
      if (token !== this.loadToken) return; // superseded by a newer navigation
      const sketch = mod.default();
      await sketch.init(this.context());
      if (token !== this.loadToken) {
        sketch.dispose();
        return;
      }
      this.current = sketch;
      this.currentId = meta.id;
      this.applySize();
    } catch (err) {
      console.error(`Failed to open sketch "${meta.id}"`, err);
      this.overlay.error(`Couldn't load "${meta.title}".`);
    }
  }

  showMenu(): void {
    ++this.loadToken;
    this.disposeCurrent();
    this.overlay.hide();
    this.menu.show();
    this.applySize();
  }

  private disposeCurrent(): void {
    if (this.current) {
      try {
        this.current.dispose();
      } catch (err) {
        console.error('Error disposing sketch', err);
      }
    }
    this.current = null;
    this.currentId = null;
  }

  private context(): SketchContext {
    const { w, h } = this.cssSize();
    return {
      renderer: this.renderer,
      input: this.input,
      idle: this.idle,
      quality: this.quality,
      rng: this.rng,
      size: { w, h, dpr: this.pixelRatio() },
    };
  }

  // --- sizing -----------------------------------------------------------

  private cssSize(): { w: number; h: number } {
    return { w: window.innerWidth, h: window.innerHeight };
  }

  private pixelRatio(): number {
    return effectiveDpr() * this.quality.scale;
  }

  private applySize(): void {
    const { w, h } = this.cssSize();
    this.renderer.setPixelRatio(this.pixelRatio());
    this.renderer.setSize(w, h, true);
    this.current?.resize(w, h);
  }

  private readonly onResize = (): void => this.applySize();

  private readonly onVisibility = (): void => {
    if (document.visibilityState === 'visible') this.last = performance.now();
  };

  dispose(): void {
    cancelAnimationFrame(this.raf);
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.disposeCurrent();
    this.input.dispose();
    this.renderer.dispose();
  }
}
